import { z } from 'zod';
import { MyInsightsVeevaOnlineUtils, type MyInsightsVeevaUtils } from './myinsights.utils';

export interface MyInsights {
  getSSOToken(): Promise<string>;
  refreshSSOToken(oldToken: string): Promise<string>;
  getUserInfo(): Promise<unknown>;
  getUserId(): Promise<string>;
}

export class MyInsightsOnline implements MyInsights {
  #utils: MyInsightsVeevaUtils;
  #veevaAuthProvider: 'ping' | 'entra' | undefined;

  constructor(veevaAuthProvider?: 'ping' | 'entra') {
    window.myinsights.init();
    this.#utils = new MyInsightsVeevaOnlineUtils();
    this.#veevaAuthProvider = veevaAuthProvider;
  }

  #getClient() {
    if (!window.ds) throw new Error('Attemping to use Veeva DataService API but the library is not available.');
    return window.ds;
  }

  getTokenProviderName() {
    const provider = this.#veevaAuthProvider;

    if (provider === 'ping') {
      return 'PingOne_Oauth';
    }
    if (provider === 'entra') {
      return 'EntraID_Oauth';
    }

    throw new Error(`Unsupported VEEVA_AUTH_PROVIDER: ${provider}`);
  }

  async getSSOToken() {
    const result = await this.#getClient().getSSOAccessToken(this.getTokenProviderName(), 'Open Id Connect');
    return result.data.token;
  }

  async refreshSSOToken(oldToken: string) {
    const result = await this.#getClient().getSSOAccessToken(this.getTokenProviderName(), 'Open Id Connect', oldToken);
    return result.data.token;
  }

  async getUserInfo() {
    const userId = await this.getUserId();

    return await this.getUserDetails([userId]);
  }

  async getUserDetails(userIds: string[]) {
    const StringField = z.object({
      dataType: z.literal('string'),
      display: z.string(),
      label: z.string(),
      value: z.string(),
    });
    const Response = z.object({
      data: z.object({
        Id: StringField,
        Department: StringField,
        Division: StringField,
        'JnJ_External_ID__c': StringField,
        'Territory_JNJ__c': StringField,
      }).array(),
    });
    type Response = z.infer<typeof Response>;

    const response = await this.#getClient().runQuery<Response>({
      object: 'User',
      fields: [
        'Id',
        'Department',
        'Division',
        'JnJ_External_ID__c',
        'Territory_JNJ__c',
      ],
      where: `Id in ${this.#utils.formatInArrayStatement(userIds)}`,
    });
    return Response.parseAsync(response).then(r => r.data);
  }

  async getUserId() {
    type Response = {
      User: {
        Id: string;
      };
    };
    return this.#getClient().getDataForCurrentObject<Response>('User', 'Id').then(result => result.User.Id);
  }
}
